import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '../../../utils/toast.jsx';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

//components
import {
  Checkbox,
  FormControlLabel,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import LoginWith from '../../../components/LoginWith/inedx';
import BaseButton from '../../../components/common/BaseButton';
import InputBox from '../../../components/Input/InputBox';

//utils
import api from '../../../utils/api';
import { formikErrorMessage, formikErrorInput } from '../../../utils/helper';
import { setCookie } from '../../../utils/cookies';
import { useConfig } from '../../../Hooks/useConfig';
import { useAuthStatus } from '../../../store/auth/useAuthStatus';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const LinkText = styled(Typography)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.secondary_70
      : theme.palette.secondary.main,
  fontWeight: 'bold',
  fontSize: '12px !important',
}));

const FormSingUp = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const history = useHistory();
  const { t } = useTranslation();
  const { baseDomain } = useConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const theme = useTheme();

  // zustand
  const { setToken } = useAuthStatus();

  //formik
  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      password: '',
      password_confirmation: '',
      email: '',
      terms: false,
    },

    validationSchema: Yup.object({
      first_name: Yup.string().required(t('sign_up.required_first_name')),
      last_name: Yup.string().required(t('sign_up.required_last_name')),
      password: Yup.string()
        .required(t('sign_up.required_password'))
        .min(8, t('sign_up.lenght_password')),
      password_confirmation: Yup.string()
        .required(t('sign_up.required_confirm_password'))
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
      email: Yup.string()
        .email(t('sign_up.inValid_email'))
        .required(t('sign_up.required_email')),
      terms: Yup.boolean()
        .oneOf([true], 'sign_up.accept_terms')
        .required('sign_up.required_terms'),
    }),

    onSubmit: (values) => {
      registerReq(values);
    },
  });

  const registerReq = async (values) => {
    setIsLoading(true);

    let userAgent = navigator.userAgent;
    let userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!executeRecaptcha) {
      console.error('Recaptcha has not been loaded');
      return;
    }

    const recaptchaToken = await executeRecaptcha('login');

    if (!recaptchaToken) {
      showErrorToast('Verification failed. Please try again.');
      return;
    }

    const register = await api.call({
      name: 'register',
      data: {
        ...values,
        email: values.email.toLowerCase(),
        user_agent: userAgent,
        user_timezone: userTimezone,
        recaptcha_token: recaptchaToken,
      },
    });

    setIsLoading(false);
    if (register.status === 200) {
      setCookie(
        'emailNotConfirmed',
        'true',
        register.data.data.expires_in,
        baseDomain
      );
      setCookie(
        'token',
        register.data.data.token,
        register.data.data.expires_in,
        baseDomain
      );
      setToken(register.data.data.token);
      if (localStorage.getItem('redirectUrl')) {
        history.push(
          '/' + localStorage.getItem('redirectUrl').replace('?redirect=', '')
        );
        localStorage.removeItem('redirectUrl');
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'loginComplete',
      });
      showSuccessToast('We have emailed your verification link!');
    }
  };

  useEffect(() => {
    if (executeRecaptcha) {
      setIsRecaptchaLoaded(true);
    }
  }, [executeRecaptcha]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <InputBox
        ErrorInput={formikErrorInput(formik, 'email')}
        errorMassage={formikErrorMessage(formik, 'email')}
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={t('sign_up.input_email_placeholder')}
        name="email"
      />

      <InputBox
        ErrorInput={formikErrorInput(formik, 'first_name')}
        errorMassage={formikErrorMessage(formik, 'first_name')}
        value={formik.values.first_name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={t('sign_up.input_first_name_placeholder')}
        name="first_name"
      />

      <InputBox
        ErrorInput={formikErrorInput(formik, 'last_name')}
        errorMassage={formikErrorMessage(formik, 'last_name')}
        value={formik.values.last_name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        placeholder={t('sign_up.input_Last_name_placeholder')}
        name="last_name"
      />

      <InputBox
        ErrorInput={formikErrorInput(formik, 'password')}
        errorMassage={formikErrorMessage(formik, 'password')}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        password
        type="password"
        placeholder={t('sign_up.input_password_placeholder')}
        name="password"
        checkStrong
      />

      <InputBox
        ErrorInput={formikErrorInput(formik, 'password_confirmation')}
        errorMassage={formikErrorMessage(formik, 'password_confirmation')}
        value={formik.values.password_confirmation}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        password
        type="password"
        placeholder={t('sign_up.input_confirem_password_placeholder')}
        name="password_confirmation"
        checkStrong
      />

      <FormControlLabel
        label={
          <>
            <Typography
              component={'lable'}
              for="checkbox_Agree"
              sx={{
                fontSize: '12px !important',
                color:
                  formik.errors.terms && formik.touched.terms
                    ? theme.palette.error.main
                    : null,
              }}
            >
              {t('sign_up.i_agree_with')}{' '}
              <LinkText
                sx={{
                  color:
                    formik.errors.terms && formik.touched.terms
                      ? theme.palette.error.main
                      : null,
                }}
                component={'a'}
                href="https://magicalapi.com/terms-of-services"
                target={'_blank'}
              >
                {t('sign_up.terms')}
              </LinkText>{' '}
              {t('sign_up.and')}{' '}
              <LinkText
                sx={{
                  color:
                    formik.errors.terms && formik.touched.terms
                      ? theme.palette.error.main
                      : null,
                }}
                component={'a'}
                href="https://magicalapi.com/privacy-policy"
                target={'_blank'}
              >
                {t('sign_up.policies')}
              </LinkText>
            </Typography>
          </>
        }
        sx={{ ml: 0, display: 'flex', alignItems: 'center', mb: 10 }}
        control={
          <Checkbox
            name="terms"
            sx={{ padding: '0px', mr: 3 }}
            checked={formik.values.terms}
            onChange={formik.handleChange}
            inputProps={{ 'aria-label': 'controlled' }}
            color={
              formik.errors.terms && formik.touched.terms ? 'error' : 'primary'
            }
          />
        }
      />

      <BaseButton
        type="submit"
        loading={isLoading}
        disabled={isLoading || !isRecaptchaLoaded}
        sx={{ width: '100%' }}
      >
        {t('button_text.sign_up')}
      </BaseButton>
      <LoginWith />
    </form>
  );
};
export default FormSingUp;
